import { useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';
import { User, Mail, Phone, Award, Edit, Building2 } from 'lucide-react';
import AuthImage from '../../components/AuthImage';
import PageHeading from '../../components/PageHeading';

function WorkerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { data: worker, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['worker', id],
    queryFn: async ({ signal }) => {
      const response = await api.get(`/workers/${id}`, { signal });
      return response.data;
    },
  });

  const { data: covered, isLoading: coveredLoading } = useQuery({
    queryKey: ['worker-buildings-covered', id],
    queryFn: async ({ signal }) => {
      const response = await api.get(`/workers/${id}/buildings-covered`, { signal });
      return response.data?.data || { access: [], default_iqp: [], open_jobs: [] };
    },
    enabled: !!id && user?.role !== 'WORKER',
  });

  const { data: ssCodesData } = useQuery({
    queryKey: ['ss-codes'],
    queryFn: async ({ signal }) => {
      try {
        const response = await api.get('/workers/ss-codes', { signal });
        return response.data?.data || [];
      } catch (e) {
        const aborted =
          e?.code === 'ECONNABORTED' || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';
        if (aborted) throw e;
        console.error('Failed to fetch SS codes:', e);
        return [];
      }
    },
  });

  const buildingRows = useMemo(() => {
    const map = new Map();
    const touch = (buildingId, patch) => {
      const key = String(buildingId || '');
      if (!key || key === 'null' || key === 'undefined') return;
      const prev = map.get(key) || {
        building_id: key,
        building_name: '',
        address: '',
        roles: new Set(),
        ss_codes: new Set(),
        open_jobs: [],
      };
      if (patch.building_name) prev.building_name = patch.building_name;
      if (patch.address) prev.address = patch.address;
      if (patch.role) prev.roles.add(patch.role);
      if (patch.ss_code) prev.ss_codes.add(String(patch.ss_code));
      if (patch.open_job) prev.open_jobs.push(patch.open_job);
      map.set(key, prev);
    };

    for (const row of covered?.access || []) {
      touch(row.building_id, {
        building_name: row.building_name,
        address: row.address,
        role: row.access_type === 'DEFAULT' ? 'App access (default)' : 'App access',
      });
    }
    for (const row of covered?.default_iqp || []) {
      touch(row.building_id, {
        building_name: row.building_name,
        address: row.address,
        role: 'Default IQP',
        ss_code: row.ss_code,
      });
    }
    for (const row of covered?.open_jobs || []) {
      touch(row.building_id, {
        building_name: row.building_name,
        address: row.address,
        role: 'Open inspection',
        ss_code: row.ss_code,
        open_job: {
          id: row.job_id,
          ss_code: row.ss_code,
          ss_name: row.ss_name,
          status: row.status,
          due_date: row.due_date,
        },
      });
    }

    return Array.from(map.values()).sort((a, b) =>
      String(a.building_name || '').localeCompare(String(b.building_name || ''))
    );
  }, [covered]);

  const getSsCodeDetails = (ssCode) => {
    if (!ssCodesData || !ssCode) return null;
    return ssCodesData.find((ss) => ss.code === ssCode);
  };

  if (isLoading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-700 font-medium">Could not load worker</p>
        <p className="text-sm text-gray-600 mt-2">{error?.message || 'Request failed'}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!worker) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Worker not found or record does not exist.</p>
        <button
          onClick={() => navigate('/people')}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Back to Staff
        </button>
      </div>
    );
  }

  return (
    <div>
      <PageHeading
        title={worker.name}
        backTo="/people"
        backLabel="Back to Staff"
        infoTitle={`${worker.name} — staff settings`}
        infoChildren={
          <>
            {worker.email ? <p>{worker.email}</p> : null}
            <p className="mt-2 text-sm text-gray-600">
              Buildings this person covers (app access, default IQP, and open inspections).
            </p>
          </>
        }
        actions={
          user?.role !== 'WORKER' ? (
            <button
              type="button"
              onClick={() => navigate(`/workers/${id}/edit`)}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Worker
            </button>
          ) : null
        }
      />

      <p className="text-sm text-gray-600 mb-6">Staff settings</p>

      <div className="mb-8 flex items-center gap-6">
        <div className="w-20 h-20 rounded-full border-2 border-gray-200 bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
          {worker.photo_url ? (
            <AuthImage
              filename={worker.photo_url}
              alt={worker.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <User className="w-10 h-10 text-gray-400" />
          )}
        </div>
      </div>

      {/* Buildings covered — settings focus */}
      {user?.role !== 'WORKER' && (
        <div className="bg-card rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-gray-500" />
              Buildings covered
            </h2>
            <span className="text-sm text-gray-500">
              {coveredLoading ? 'Loading…' : `${buildingRows.length} building${buildingRows.length === 1 ? '' : 's'}`}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            App access, default IQP for SS codes, and open assigned inspections.
          </p>
          {coveredLoading ? (
            <p className="text-sm text-gray-500 py-6 text-center">Loading buildings…</p>
          ) : buildingRows.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>No buildings linked yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {buildingRows.map((b) => (
                <div key={b.building_id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Link
                        to={`/buildings/${b.building_id}`}
                        className="font-medium text-primary-800 hover:underline"
                      >
                        {b.building_name || `Building #${b.building_id}`}
                      </Link>
                      {b.address ? <p className="text-sm text-gray-500 mt-0.5">{b.address}</p> : null}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {[...b.roles].map((role) => (
                        <span
                          key={role}
                          className="px-2 py-0.5 text-xs font-medium rounded bg-slate-100 text-slate-800"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                  {b.ss_codes.size > 0 && (
                    <p className="text-xs text-gray-600 mt-2">
                      SS: {[...b.ss_codes].sort().join(', ')}
                    </p>
                  )}
                  {b.open_jobs.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {b.open_jobs.map((j) => (
                        <li key={j.id} className="text-sm">
                          <Link to={`/jobs/${j.id}`} className="text-primary-700 hover:underline">
                            {j.ss_code}
                            {j.ss_name ? ` — ${j.ss_name}` : ''}
                          </Link>
                          <span className="text-gray-500 ml-2">{j.status}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Worker Info */}
      <div className="bg-card rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Worker Information</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start">
            <Mail className="w-5 h-5 text-gray-400 mr-3 mt-1" />
            <div>
              <p className="text-sm font-medium text-gray-500">Email</p>
              <p className="text-gray-900">{worker.email}</p>
            </div>
          </div>
          {worker.phone && (
            <div className="flex items-start">
              <Phone className="w-5 h-5 text-gray-400 mr-3 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-500">Phone</p>
                <p className="text-gray-900">{worker.phone}</p>
              </div>
            </div>
          )}
          {worker.license_number && (
            <div className="flex items-start">
              <Award className="w-5 h-5 text-gray-400 mr-3 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-500">License Number</p>
                <p className="text-gray-900">{worker.license_number}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* IQP Grades / SS Codes */}
      <div className="bg-card rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">SS Codes / IQP Grades</h2>
          {user?.role !== 'WORKER' && (
            <button
              onClick={() => navigate(`/workers/${id}/edit`)}
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              SS Code management
            </button>
          )}
        </div>
        {worker.iqp && worker.iqp.length > 0 ? (
          <div className="space-y-3">
            {worker.iqp.map((iqp) => {
              const ssCodeDetails = getSsCodeDetails(iqp.ss_code);
              return (
                <div key={iqp.id || `${iqp.worker_id}-${iqp.ss_code}`} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">{iqp.ss_code}</span>
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                          Grade {iqp.grade}
                        </span>
                      </div>
                      {ssCodeDetails && (
                        <div className="mt-1">
                          <p className="text-sm font-medium text-gray-700">{ssCodeDetails.name}</p>
                          {ssCodeDetails.description && (
                            <p className="text-xs text-gray-500 mt-0.5">{ssCodeDetails.description}</p>
                          )}
                          {ssCodeDetails.applicable_standards && (
                            <p className="text-xs text-gray-400 mt-1">
                              Standards: {ssCodeDetails.applicable_standards}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    {iqp.expiry_date && (
                      <div className="text-right ml-4">
                        <span className="text-sm text-gray-500">
                          Expires: {new Date(iqp.expiry_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Award className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>No SS codes assigned</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkerDetail;
